import { useEffect, useState } from "react";
import Footer from "../../components/Footer";
import SubHeader from "../../components/SubHeader";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { setGameMode, setPlayerView, setTargets, setTimeAndDay } from "../../store/gameSlice";
import { GameModeEnum } from "../../types/gameModeEnum";
import useWebSocket from "react-use-websocket";
import RolePopup from "../../components/RolePopup";
import ConfirmPopup from "../../components/ConfirmPopup";
import MafiaService from "../../services/mafiaService";
import ScenarioPopup from "../../components/ScenarioPopup";
import { IPlayerView } from "../../types/IPlayerView";
import { showErrorToast } from "../../store/messageSlice";
import ScenarioUtils from "../../utils/scenarioUtils";
import { PlayerStatusEnum } from "../../types/playerStatusEnum";

function Night() {
    const dispatch = useDispatch();
    const { serverName, playerName, role } = useSelector((state: RootState) => state.general);
    const { day, time, playerView, targets, playerRole } = useSelector((state: RootState) => state.game);
    const [ rolePopupOpen, setRolePopupOpen] = useState(false);
    const [ scenarioPopupOpen, setScenarioPopupOpen] = useState(false);
    const [ confirmEndNightPopup, setConfirmEndNighPopup] = useState(false);
    const [ targetCount, setTargetCount ] = useState(1); //max number of targets

    const { sendJsonMessage, lastJsonMessage, readyState } = useWebSocket(
        `${process.env.REACT_APP_SERVER_URL}/game_time/${serverName}`,
        {
            share: false,
            shouldReconnect: () => true,
        },
    )

    useEffect(() => {
        if (lastJsonMessage) {
            const { time_of_day, time_left, day } = lastJsonMessage;
            if (time_of_day === 'Day') {
                dispatch(setGameMode(GameModeEnum.Day));
            }
            dispatch(setTimeAndDay({ time: +time_left, day: +day }));
        }
    }, [lastJsonMessage]);

    useEffect(() => {
        pollPlayers();
    }, []);

    useEffect(() => {
        if(playerView?.role_name?.toLowerCase() === 'busdriver') {
            setTargetCount(2);
        } else {
            setTargetCount(1);
        }
    }, [playerView])

    
    const pollPlayers = () => {
        MafiaService.PlayerView(serverName, playerName, (is_ok, payload) => {
            if (is_ok) {
                const playerView = payload as IPlayerView;
                dispatch(setPlayerView(playerView));
                if (process.env.REACT_APP_DEV_MODE === 'true') {
                    return;
                }
                if (playerView.winners?.length > 0) {
                    dispatch(setGameMode(GameModeEnum.End));
                }
            } else {
                dispatch(showErrorToast(payload))
            }
        })
    }

    const confirmPopup = (confirmCallback: () => void, cancelCallback: () => void) => {
        return <div className="mafia-container-wrapper">
            <div className="mafia-container">
                <ConfirmPopup message="Are you sure?" onConfirm={confirmCallback} onCancel={cancelCallback} />
            </div>
        </div>
    }

    if(confirmEndNightPopup) {
        return confirmPopup(() => MafiaService.EndNight(serverName), () => setConfirmEndNighPopup(false));
    }

    if (rolePopupOpen) {
        return <RolePopup role={playerView?.role_name || ''} onCancel={()=>setRolePopupOpen(false)} exeTarget={playerView?.exe_target} />
    }

    if (scenarioPopupOpen) {
        return <ScenarioPopup onCancel={()=>setScenarioPopupOpen(false)}/>
    }

    if(!playerView) {
        return <></>;
    }

    const getPlayerList = () => {
        const onTarget = (e: any) => {
            const pos = +e.target.value
            const player = playerView.other_players_state[e.target.value].name;

            if(targets.indexOf(pos) === -1) {
                MafiaService.Act(serverName, playerView.position, pos, (is_ok, payload) => {
                    if (is_ok) {
                        console.log(`${player} targeted`);
                    } else {
                        //hahah this way we could know who's from Mafia xD
                        //dispatch(showErrorToast(payload))
                    }
                    if(targets.length >= targetCount) {
                        const newTargets = [...targets];
                        newTargets.splice(0, 1);
                        dispatch(setTargets([...newTargets, pos]));
                    } else {
                        dispatch(setTargets([ ...targets, pos]));
                    }
                });
            } else {
                MafiaService.RemoveAct(serverName, playerView.position, pos, (is_ok, payload) => {
                    if (is_ok) {
                        console.log(`${player} untargets`);
                    }
                    const newTargets = [...targets];
                    newTargets.splice(targets.indexOf(pos), 1);
                    dispatch(setTargets(newTargets));
                });                
            }
        }

        return <div className="mafia-player-list">
            {playerView.other_players_state.map((player, index) => {
                const oddEven = index % 2 == 1 ? 'odd reverted' : 'even';
                let buttonClass = '';
                const playerStatus = ScenarioUtils.GetOtherPlayerStatus(playerName === player.name, playerRole, playerView?.is_dead === true, player.role_name, player.is_dead);
                if(!player.is_targetable) {
                    buttonClass = 'disabled hidden';
                } else if(targets.indexOf(index) !== -1) {
                    buttonClass = 'targeted';
                }
                const playerClass = playerStatus !== PlayerStatusEnum.Hidden ? `${player.role_name} ` : '';
                const nameClass = `${player.is_dead ? 'dead' : playerStatus === PlayerStatusEnum.Mafia ? `mark` : ''}`;
                return <div key={index} className={`lobby-player-row ${oddEven}`}>
                    <div className={`pos night ${nameClass}`}>{index + 1}</div>
                    <div className={`name night ${nameClass}`}>{playerClass}{player.name}</div>
                    <div className={`button night`}>
                        <button value={index} onClick={onTarget} className={buttonClass}>Target</button>
                    </div>
                </div>
            })}
        </div>
    }

    return <div className="mafia-container-wrapper">
        <div className="mafia-container">
            <SubHeader>
                <div className="game-sub-header">
                    <div className="day">
                        Night <span>{day}</span>
                    </div>
                    <div className="time">
                        Time Left: <span>{time}</span>
                    </div>
                </div>
            </SubHeader>
            <div className="mafia-lobby-container mafia-list-container">
                {getPlayerList()}
            </div>
        </div>
        <Footer>
            <button onClick={() => setScenarioPopupOpen(true)}>Info</button>
            <button onClick={() => setRolePopupOpen(true)}>Your Role</button>
            {role === 'admin' && <button onClick={() => setConfirmEndNighPopup(true)}>End Night</button>}
        </Footer>
    </div>
}

export default Night;